import {
  KoDrawer,
  KoLoader,
  KoTable,
  KoText,
  KoTitle,
  KoTag,
  KoEmpty,
  KoPagination,
  KoCharts,
  KoSkeleton
} from 'packages';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { MonitoringAPI } from '../services/monitoring.service';
import { COLUMNS_SERVICE_HEALTH, HEALTH_STATUS_CONFIG } from '../constants';
import { metricsLineChart } from '../charts/metricsLineChart';
import { resourceUsageChart } from '../charts/resourceUsageChart';
import MonitoringSubNav from './MonitoringSubNav';
import styles from './monitoring.module.scss';

const MOCK_SERVICES = [
  { serviceId: 'svc-001', serviceName: 'api-gateway', version: '3.2.1', status: 'healthy', errorRate: 0.12, p95Latency: 45, cpuUsage: 32.5, memoryUsage: 58.2, connections: 1240 },
  { serviceId: 'svc-002', serviceName: 'auth-service', version: '2.8.0', status: 'healthy', errorRate: 0.05, p95Latency: 28, cpuUsage: 18.3, memoryUsage: 42.1, connections: 890 },
  { serviceId: 'svc-003', serviceName: 'payment-processor', version: '4.1.0', status: 'degraded', errorRate: 2.45, p95Latency: 320, cpuUsage: 78.9, memoryUsage: 85.3, connections: 560 },
  { serviceId: 'svc-004', serviceName: 'notification-hub', version: '1.5.3', status: 'healthy', errorRate: 0.08, p95Latency: 15, cpuUsage: 12.1, memoryUsage: 35.7, connections: 2100 },
  { serviceId: 'svc-005', serviceName: 'user-service', version: '3.0.2', status: 'healthy', errorRate: 0.22, p95Latency: 52, cpuUsage: 41.2, memoryUsage: 62.8, connections: 1580 },
  { serviceId: 'svc-006', serviceName: 'search-engine', version: '2.3.1', status: 'critical', errorRate: 8.71, p95Latency: 890, cpuUsage: 95.2, memoryUsage: 92.4, connections: 340 },
  { serviceId: 'svc-007', serviceName: 'file-storage', version: '1.9.4', status: 'healthy', errorRate: 0.03, p95Latency: 110, cpuUsage: 25.8, memoryUsage: 71.5, connections: 430 },
  { serviceId: 'svc-008', serviceName: 'analytics-pipeline', version: '2.1.0', status: 'degraded', errorRate: 1.85, p95Latency: 245, cpuUsage: 65.4, memoryUsage: 78.9, connections: 210 },
  { serviceId: 'svc-009', serviceName: 'cache-layer', version: '1.2.0', status: 'healthy', errorRate: 0.01, p95Latency: 3, cpuUsage: 8.5, memoryUsage: 45.2, connections: 5200 },
  { serviceId: 'svc-010', serviceName: 'email-service', version: '1.7.2', status: 'healthy', errorRate: 0.15, p95Latency: 180, cpuUsage: 22.3, memoryUsage: 38.6, connections: 150 }
];

const MOCK_METRICS = {
  requestData: [
    { time: '14:00', http_requests: 1240 }, { time: '14:05', http_requests: 1380 },
    { time: '14:10', http_requests: 1520 }, { time: '14:15', http_requests: 1100 },
    { time: '14:20', http_requests: 1680 }, { time: '14:25', http_requests: 1450 },
    { time: '14:30', http_requests: 1320 }, { time: '14:35', http_requests: 1590 },
    { time: '14:40', http_requests: 1270 }, { time: '14:45', http_requests: 1410 },
    { time: '14:50', http_requests: 1550 }, { time: '14:55', http_requests: 1480 }
  ],
  resourceData: [
    { time: '14:00', cpu: 32, memory: 58 }, { time: '14:05', cpu: 35, memory: 59 },
    { time: '14:10', cpu: 41, memory: 61 }, { time: '14:15', cpu: 38, memory: 60 },
    { time: '14:20', cpu: 45, memory: 63 }, { time: '14:25', cpu: 42, memory: 62 },
    { time: '14:30', cpu: 36, memory: 59 }, { time: '14:35', cpu: 39, memory: 61 },
    { time: '14:40', cpu: 33, memory: 58 }, { time: '14:45', cpu: 37, memory: 60 },
    { time: '14:50', cpu: 40, memory: 62 }, { time: '14:55', cpu: 34, memory: 59 }
  ]
};

function ServiceHealth() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [services, setServices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ size: 20, page: 1 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [serviceMetrics, setServiceMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/monitoring`
    : '/monitoring';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Monitoring', path: basePath },
      { title: 'Service Health' }
    ]));
  }, [workspaceId]);

  useEffect(() => {
    fetchServices();
  }, [workspaceId, pagination, searchTerm, statusFilter]);

  const fetchServices = () => {
    setLoading(true);
    const params = {
      page: pagination.page,
      limit: pagination.size,
      ...(searchTerm && { search: searchTerm }),
      ...(statusFilter && { status: statusFilter })
    };
    MonitoringAPI.getServiceHealth(params).then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_SERVICES;
        setServices(data);
        setTotalCount(res?.totalCount || data.length);
        setLoading(false);
      },
      () => {
        setServices(MOCK_SERVICES);
        setTotalCount(MOCK_SERVICES.length);
        setLoading(false);
      }
    );
  };

  const openServiceDetail = (record) => {
    setSelectedService(record);
    setDrawerOpen(true);
    setMetricsLoading(true);
    MonitoringAPI.getLiveMetrics(record.serviceId || record._id, { timeRange: '1h' }).then(
      (data) => { setServiceMetrics(data?.requestData ? data : MOCK_METRICS); setMetricsLoading(false); },
      () => { setServiceMetrics(MOCK_METRICS); setMetricsLoading(false); }
    );
  };

  const summary = {
    healthy: services.filter(s => s.status === 'healthy').length,
    degraded: services.filter(s => s.status === 'degraded').length,
    critical: services.filter(s => s.status === 'critical').length
  };

  const filterOptions = [
    { key: 'all', label: 'All' },
    { key: 'healthy', label: 'Healthy' },
    { key: 'degraded', label: 'Degraded' },
    { key: 'critical', label: 'Critical' }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <MonitoringSubNav activeKey='service-health' />

      <KoToolBar
        title='Service Health'
        search={{
          visible: true,
          placeholder: 'Search by service name...',
          onChange: (val) => { setSearchTerm(val); setPagination(p => ({ ...p, page: 1 })); }
        }}
        filter={{
          visible: true,
          options: filterOptions,
          onClick: (key) => {
            setStatusFilter(key === 'all' ? null : key);
            setPagination(p => ({ ...p, page: 1 }));
          }
        }}
        buttonList={[]}
      />

      {/* Health Summary */}
      <div className={styles.healthSummary}>
        <div className={styles.healthSummaryCard}>
          <div className={`${styles.healthDot} ${styles.healthDotGreen}`} />
          <div>
            <div className={styles.healthCardValue}>{summary.healthy}</div>
            <div className={styles.healthCardLabel}>Healthy</div>
          </div>
        </div>
        <div className={styles.healthSummaryCard}>
          <div className={`${styles.healthDot} ${styles.healthDotYellow}`} />
          <div>
            <div className={styles.healthCardValue}>{summary.degraded}</div>
            <div className={styles.healthCardLabel}>Degraded</div>
          </div>
        </div>
        <div className={styles.healthSummaryCard}>
          <div className={`${styles.healthDot} ${styles.healthDotRed}`} />
          <div>
            <div className={styles.healthCardValue}>{summary.critical}</div>
            <div className={styles.healthCardLabel}>Critical</div>
          </div>
        </div>
      </div>

      {services.length === 0 && !isLoading && (
        <KoEmpty description='No services found' />
      )}

      {services.length > 0 && (
        <>
          <KoTable
            maxHeight={520}
            dataSource={services}
            columns={COLUMNS_SERVICE_HEALTH}
            rowKey={(row) => row.serviceId || row._id}
            onRow={(record) => ({
              onClick: () => openServiceDetail(record)
            })}
          />
          <KoPagination
            totalCount={totalCount}
            pageSize={pagination.size}
            currentPage={pagination.page}
            onChange={(page, size) => setPagination({ page, size })}
          />
        </>
      )}

      {/* Service Detail Drawer */}
      <KoDrawer
        title={selectedService?.serviceName || 'Service Detail'}
        open={isDrawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedService(null); setServiceMetrics(null); }}
        width={'min(700px, 95vw)'}
      >
        {selectedService && (
          <div className={styles.healthDetailDrawer}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <KoTitle level={4} text={selectedService.serviceName} />
              <KoTag color={HEALTH_STATUS_CONFIG[selectedService.status]?.color}>
                {HEALTH_STATUS_CONFIG[selectedService.status]?.label}
              </KoTag>
              <KoText className={styles.monoSmall} text={`v${selectedService.version}`} />
            </div>

            <div className={styles.logMetaGrid}>
              <div className={styles.logMetaItem}>
                <span className={styles.logDetailLabel}>Error Rate</span>
                <span className={styles.logDetailValue}>{selectedService.errorRate?.toFixed(2)}%</span>
              </div>
              <div className={styles.logMetaItem}>
                <span className={styles.logDetailLabel}>P95 Latency</span>
                <span className={styles.logDetailValue}>{selectedService.p95Latency}ms</span>
              </div>
              <div className={styles.logMetaItem}>
                <span className={styles.logDetailLabel}>CPU</span>
                <span className={styles.logDetailValue}>{selectedService.cpuUsage?.toFixed(1)}%</span>
              </div>
              <div className={styles.logMetaItem}>
                <span className={styles.logDetailLabel}>Memory</span>
                <span className={styles.logDetailValue}>{selectedService.memoryUsage?.toFixed(1)}%</span>
              </div>
            </div>

            <div>
              <KoTitle level={5} text='Request Rate (Last 1h)' className={styles.chartTitle} />
              {metricsLoading && <KoSkeleton active paragraph={{ rows: 6 }} />}
              {!metricsLoading && serviceMetrics?.requestData && (
                <div style={{ height: '24rem' }}>
                  <KoCharts option={metricsLineChart(serviceMetrics.requestData, ['http_requests'])} />
                </div>
              )}
            </div>

            <div>
              <KoTitle level={5} text='Resource Usage (Last 1h)' className={styles.chartTitle} />
              {metricsLoading && <KoSkeleton active paragraph={{ rows: 6 }} />}
              {!metricsLoading && serviceMetrics?.resourceData && (
                <div style={{ height: '24rem' }}>
                  <KoCharts option={resourceUsageChart(serviceMetrics.resourceData)} />
                </div>
              )}
            </div>
          </div>
        )}
      </KoDrawer>
    </div>
  );
}

export default ServiceHealth;
